import { StaggeredAnimation } from "#components/animation/StaggeredAnimation.tsx";
import { StaggeredAnimationItem } from "#components/animation/StaggeredAnimationItem.tsx";
import { LoadingDots } from "#components/ui/text-loading/LoadingDots.tsx";
import { WorkleapIcon } from "#components/ui/WorkleapIcon.tsx";
import { ThinkingStepItem } from "#features/conversations/components/conversation-view/ThinkingStepItem.tsx";
import { useThinkingStepDefaults } from "#features/conversations/hooks/useThinkingStepDefaults.ts";
import type { ConversationMessageModel } from "#features/conversations/types/ConversationMessageModel.ts";
import { ThinkingStepStates } from "#features/conversations/types/ThinkingStep.ts";
import { isGenerating, isInterrupted } from "#features/conversations/utils/assistantStatus.ts";
import { mergeThinkingSteps } from "#features/conversations/utils/mergeThinkingSteps.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { Button, Disclosure, DisclosurePanel, Div, Text, ToggleArrow } from "@hopper-ui/components";
import { useEffect, useMemo, useRef, useState } from "react";
import "./AssistantMessageThinkingSteps.css";

interface AssistantMessageThinkingStepsProps {
    message: ConversationMessageModel;
}

export const AssistantMessageThinkingSteps = ({ message }: AssistantMessageThinkingStepsProps) => {
    const { tShared } = useSharedTranslation();
    const { getDefaultStepTitle } = useThinkingStepDefaults();
    const { trackEvent } = useTracking();
    const thinkingSteps = useMemo(() => message.thinkingSteps ?? [], [message.thinkingSteps]);

    const mergedThinkingSteps = useMemo(() => mergeThinkingSteps(thinkingSteps), [thinkingSteps]);

    const isMessageGenerating = isGenerating(message.assistantStatus);
    const [isExpanded, setIsExpanded] = useState(false);
    const userOverrideRef = useRef<boolean | null>(null);

    // Auto-expand parent when thinking starts
    useEffect(() => {
        if (isMessageGenerating && mergedThinkingSteps.length > 0 && userOverrideRef.current !== false) {
            setIsExpanded(true);
        }
    }, [isMessageGenerating, mergedThinkingSteps.length]);

    // Auto-collapse parent when answer appears
    useEffect(() => {
        const shouldKeepExpanded = isInterrupted(message.assistantStatus);

        if (!isMessageGenerating && message.content && isExpanded && userOverrideRef.current !== true && !shouldKeepExpanded) {
            setIsExpanded(false);
        }
    }, [isMessageGenerating, message.content, message.assistantStatus, isExpanded]);

    const handleExpandedChange = (expanded: boolean) => {
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Assistant Message",
            Trigger: expanded ? "Expand Thought Process" : "Collapse Thought Process"
        });

        setIsExpanded(expanded);

        if (!isMessageGenerating) {
            userOverrideRef.current = expanded;
        } else {
            // Re-expanding during generation resets to auto; collapsing locks it closed
            userOverrideRef.current = expanded ? null : false;
        }
    };

    const completedStepsCount = useMemo(
        () => mergedThinkingSteps.reduce((n, s) => s.state === ThinkingStepStates.completed ? n + 1 : n, 0),
        [mergedThinkingSteps]
    );

    const currentStep = mergedThinkingSteps.at(-1);

    const dynamicHeaderText = useMemo(() => {
        if (!isMessageGenerating || !currentStep) {
            return tShared("assistantThinkingTimeline");
        }

        return currentStep.title ?? getDefaultStepTitle(currentStep.code, currentStep.sourceCount);
    }, [currentStep, isMessageGenerating, tShared, getDefaultStepTitle]);

    if (mergedThinkingSteps.length === 0) {
        return null;
    }

    return (
        <Disclosure
            isExpanded={isExpanded}
            onExpandedChange={handleExpandedChange}
            textAlign="left"
            justifyContent="space-between"
            className="wai-assistant-thinking-steps"
            UNSAFE_marginLeft="-0.5rem"
        >
            <Button
                slot="trigger"
                size="sm"
                variant="ghost-secondary"
                justifyContent="flex-start"
                textAlign="left"
                gap="inline-sm"
                height="auto"
                UNSAFE_minHeight="2rem"
                maxWidth="100%"
                marginLeft="inline-xs"
            >
                {isMessageGenerating && <WorkleapIcon className="wai-thinking-mark" aria-hidden="true" focusable="false" />}
                <Div>
                    <Text
                        fontWeight="core_400"
                        color="neutral"
                        size="sm"
                        style={{ textWrap: "wrap" }}
                        className={isMessageGenerating ? "wai-shimmer-text" : undefined}
                    >
                        {dynamicHeaderText}{" "}
                    </Text>
                    {!isMessageGenerating && ` (${tShared("assistantThinkingStepsCompletedCount", { count: completedStepsCount })})`}
                    {isMessageGenerating && <LoadingDots delayMs={160} />}
                </Div>
                <Div width="core_160" height="core_160" textAlign="center">
                    <ToggleArrow
                        transformOrigin="center center"
                        color="neutral"
                        transition="transform 180ms ease-out"
                        UNSAFE_width="0.75rem"
                        order={0}
                    />
                </Div>
            </Button>
            <DisclosurePanel
                paddingY={0}
            >
                <StaggeredAnimation
                    display="flex"
                    flexDirection="column"
                    gap="inline-sm"
                    paddingY="inset-sm"
                    animation="wai-reveal"
                >
                    {mergedThinkingSteps.map(step => (
                        <StaggeredAnimationItem
                            key={step.id}
                        >
                            <ThinkingStepItem
                                step={step}
                                isMessageGenerating={isMessageGenerating}
                            />
                        </StaggeredAnimationItem>
                    ))}
                </StaggeredAnimation>
            </DisclosurePanel>
        </Disclosure>
    );
};
